/**
 * Thin data-access layer for the Order Booking CST / ops admin panel.
 *
 * Mirrors the sibling content-writer panel's `api.js` exactly in shape: the
 * shared axios wrapper (`src/services/api.js`) resolves with the parsed
 * envelope `{ code, status, message, data }` on success, and with the raw
 * axios error *response* on an HTTP error (so the envelope sits under
 * `.data`). `unwrap` collapses both into "payload or throw".
 *
 * There is deliberately no `fetchDetail` / `fetchTimeline` here: this admin
 * view's `/list` row already carries everything the drawer renders, including
 * the full `variations` array, so a detail round-trip would be dead weight.
 */

import API from '../../../../services/api';
import axios from '../../../../services/axios';
import { ENDPOINTS, PAGE_SIZE } from './config';

export const isCancel = (err) => axios.isCancel(err);

export const createCancelSource = () => axios.CancelToken.source();

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Pull the most useful message out of either envelope shape. */
export const errorMessageOf = (res, fallback = GENERIC_ERROR) => {
  if (!res) return fallback;
  if (res instanceof Error) return res.message || fallback;
  return res?.data?.message || res?.message || fallback;
};

const unwrap = (res, fallback) => {
  if (res && res.code === 200) return res.data;
  const err = new Error(errorMessageOf(res, fallback));
  err.payload = res;
  throw err;
};

const post = async ({ url, data, cancelToken, headers, onUploadProgress, fallback }) => {
  const res = await API({
    url,
    method: 'POST',
    responseType: 'json',
    data,
    cancelToken,
    headers,
    onUploadProgress,
  });
  return unwrap(res, fallback);
};

/**
 * `[{ value: content_writer_id, label: name }]` — already option-shaped, so the
 * assign modal can feed it straight to a `<select>`. Takes no filters; the
 * body is an empty object because the wrapper always POSTs.
 */
export const fetchContentWriters = (cancelToken) =>
  post({
    url: ENDPOINTS.contentWriters,
    data: {},
    cancelToken,
    fallback: 'Unable to load the content writers.',
  });

/** `{ unassigned, assigned, picked, submitted, rejected, approved, total }` */
export const fetchCounts = (filters, cancelToken) =>
  post({
    url: ENDPOINTS.counts,
    cancelToken,
    fallback: 'Unable to load the tab counts.',
    data: {
      sub_type: filters?.sub_type || null,
      query: String(filters?.query || '').trim(),
      start_date: filters?.start_date || null,
      end_date: filters?.end_date || null,
    },
  });

/** `{ data: Row[], total, hasMore }` — `page` is 0-based. */
export const fetchList = ({ tab, page, limit = PAGE_SIZE, ...filters }, cancelToken) =>
  post({
    url: ENDPOINTS.list,
    cancelToken,
    fallback: 'Unable to load creatives.',
    data: {
      tab,
      page,
      limit,
      sub_type: filters?.sub_type || null,
      query: String(filters?.query || '').trim(),
      start_date: filters?.start_date || null,
      end_date: filters?.end_date || null,
      sort_by_latest_activity: !!filters?.sort_by_latest_activity,
    },
  });

/**
 * `{ bsc_id, content_writer_id, content_writer_name, status: 1, logs }`.
 *
 * Upsert — the same call assigns a fresh creative and re-assigns one that
 * already has a writer, at any status. `instructions` is optional free text
 * for the writer and is trimmed here, at the API boundary, so the textarea
 * keeps whatever whitespace the user is mid-typing.
 */
export const assignContentWriter = (bsc_id, content_writer_id, instructions) =>
  post({
    url: ENDPOINTS.assign,
    data: {
      bsc_id,
      content_writer_id,
      instructions: String(instructions || '').trim(),
    },
    fallback: 'Unable to assign this creative.',
  });

/**
 * `{ bsc_id, status: 5, logs }`.
 *
 * `variation_index` is the 0-based position of the chosen variation inside the
 * row's own `variations` array — the backend copies that variation's content
 * onto the creative itself. We never send the draft text back up; the server
 * already has it, and echoing it would let a stale drawer overwrite a newer
 * submission.
 */
export const approveContent = (bsc_id, variation_index) =>
  post({
    url: ENDPOINTS.approve,
    data: { bsc_id, variation_index: Number(variation_index) },
    fallback: 'Unable to approve this content.',
  });

/** `{ bsc_id, status: 4, logs }`. The reason is shown to the content writer. */
export const rejectContent = (bsc_id, reason) =>
  post({
    url: ENDPOINTS.reject,
    data: { bsc_id, reason: String(reason || '').trim() },
    fallback: 'Unable to send this back for rework.',
  });

/** `Log[]` — full per-creative activity log, used by the drawer's request review only. */
export const fetchLogs = (bsc_id, cancelToken) =>
  post({
    url: ENDPOINTS.logs,
    data: { bsc_id },
    cancelToken,
    fallback: 'Unable to load the activity log.',
  });

/**
 * `{ bsc_id, request_bscel_id, logs }`. Log-only for now — approving a pending
 * creative-type-change or design-change request writes a `request_approved`
 * log row; it does not touch `booking_slot_creatives` itself.
 */
export const approveRequest = (bsc_id, request_bscel_id) =>
  post({
    url: ENDPOINTS.approveRequest,
    data: { bsc_id, request_bscel_id },
    fallback: 'Unable to approve this request.',
  });

/**
 * `{ bsc_id, request_bscel_id, logs }`. Log-only mirror of `approveRequest` —
 * rejecting a pending creative-type-change or design-change request writes a
 * `request_rejected` log row; it does not touch `booking_slot_creatives`.
 */
export const rejectRequest = (bsc_id, request_bscel_id) =>
  post({
    url: ENDPOINTS.rejectRequest,
    data: { bsc_id, request_bscel_id },
    fallback: 'Unable to reject this request.',
  });

/** `{ bsc_id, logs: Log[] }`. No status gate. */
export const sendMessage = (bsc_id, message) =>
  post({
    url: ENDPOINTS.message,
    data: { bsc_id, message },
    fallback: 'Unable to send your message.',
  });

/**
 * `{ data: Version[] }` — one entry per historical version of this creative's
 * content, each `{ version, isCurrent, createdAt, summary, userType, userName,
 * creativeType, subType, fields: { creativeName, headLine, description,
 * textInLink, imgUrls, videoUrls } }`. Read-only — the drawer's Versions tab.
 */
export const fetchVersions = (bsc_id, cancelToken) =>
  post({
    url: ENDPOINTS.versions,
    data: { bsc_id },
    cancelToken,
    fallback: 'Unable to load the version history.',
  });

/** `{ bsc_id, url, logs: Log[] }`. No status gate. */
export const uploadAttachment = ({ bsc_id, file, message, onUploadProgress }) => {
  const form = new FormData();
  form.append('files', file);
  form.append('bsc_id', bsc_id);
  if (message) form.append('message', message);
  return post({
    url: ENDPOINTS.uploadAttachment,
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
    fallback: 'Upload failed. Please try again.',
  });
};
